import { useCallback, useMemo, useState } from "react";
import {
  Box,
  type Omittable,
  Separator,
  WidgetCard,
  type WidgetInstance,
} from "@salestrak/ui";
import { useCollapsedSummarySections } from "#app/lib/hooks/use-collapsed-summary-sections";

export type SummaryWidgetStackItem = WidgetInstance & Omittable<{
  collapsible?: boolean;
  hideHeader?: boolean;
  /** The section may be left off the summary by the entity's type. Unset,
   *  the section is always on: it can move, never hide. */
  hideable?: boolean;
  /** The section is not offered yet: never rendered, never listed on the
   *  type, whatever a saved layout says. The string is the reason. */
  locked?: string;
}>;

const NO_ORDER: string[] = [];
const NO_HIDDEN: Record<string, boolean> = {};

/**
 * A saved widget order, replayed against the widgets that exist today: types
 * that went away drop out, and types the saved order never saw are spliced in
 * at their default neighborhood — directly after the nearest default-order
 * neighbor the saved order already carries. Appending them to the bottom
 * instead would strand a new widget far from the one it was designed to sit
 * under, and every type that ever set an order would show it there.
 */
export function reconcileWidgetOrder(
  order: unknown,
  widgets: readonly SummaryWidgetStackItem[],
) {
  const knownTypes = widgets.map((widget) => widget.type);
  const knownTypeSet = new Set(knownTypes);
  const saved: string[] = [];
  (Array.isArray(order) ? order : []).forEach((type) => {
    if (typeof type === "string" && knownTypeSet.has(type) && !saved.includes(type)) {
      saved.push(type);
    }
  });
  const savedSet = new Set(saved);
  const reconciled = [...saved];
  knownTypes.forEach((type, index) => {
    if (savedSet.has(type)) return;
    // Walk backwards through the default order for the closest widget already
    // placed — including new ones placed earlier in this pass, so a run of new
    // widgets keeps its default sequence.
    let anchor = -1;
    for (let before = index - 1; before >= 0; before -= 1) {
      const found = reconciled.indexOf(knownTypes[before]!);
      if (found >= 0) {
        anchor = found;
        break;
      }
    }
    reconciled.splice(anchor + 1, 0, type);
  });
  return reconciled;
}

/**
 * One stacked section. An EMPTY section is not a section yet: its card is one
 * "Add …" action row, and those rows sit together UNDER every section that
 * has content, in the default order — nothing to read until there is
 * something to see. The card reports which it is (`onActionRowChange`) and
 * the stack orders on that. Both shapes are THIS component in ONE list, so a
 * section that fills or empties moves without remounting — a remount would
 * report "content" on mount, before its body registers the action, and flip
 * straight back.
 */
function SummaryWidgetStackCard({
  instance,
  collapsed,
  onCollapsedChange,
  onActionRowChange,
}: {
  instance: SummaryWidgetStackItem;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onActionRowChange: (type: string, actionRow: boolean) => void;
}) {
  const reportActionRow = useCallback(
    (actionRow: boolean) => onActionRowChange(instance.type, actionRow),
    [instance.type, onActionRowChange],
  );

  return (
    <Box direction="column">
      <WidgetCard
        instance={instance}
        canManage={false}
        canArrange={false}
        presentation="stack"
        surface="canvas"
        collapsible={instance.collapsible}
        hideHeader={instance.hideHeader}
        collapsed={collapsed}
        onCollapsedChange={onCollapsedChange}
        onActionRowChange={reportActionRow}
      />
    </Box>
  );
}

/**
 * Everything a summary widget SURFACE needs: which widgets it can show and
 * the order and visibility its entity's type decided for everyone. The
 * viewer's folds come from their account preference, not from props.
 */
export type SummaryWidgetSurfaceProps = Omittable<{
  fixedWidget: SummaryWidgetStackItem;
  additionalFixedWidgets?: SummaryWidgetStackItem[];
  sortableWidgets: SummaryWidgetStackItem[];
  /** The type's section order — the read order for everyone. Types the order
   *  never saw splice in at their default place (`reconcileWidgetOrder`). */
  sectionOrder?: readonly string[];
  /** Which sections are off, by type. Missing = on. */
  hidden?: Record<string, boolean>;
}>;

export type SummaryWidgetSelection = ReturnType<typeof useSummaryWidgetSelection>;

/**
 * Which widgets a summary surface shows and in what sequence: the type's
 * order and visibility, then the sections with content ahead of the empty
 * ones.
 */
export function useSummaryWidgetSelection({
  sortableWidgets,
  sectionOrder = NO_ORDER,
  hidden: hiddenProp = NO_HIDDEN,
}: Pick<SummaryWidgetSurfaceProps, "sortableWidgets" | "sectionOrder" | "hidden">) {
  // A locked section is hidden whatever the layout says — a row written
  // before the lock must not surface a section that is not offered yet.
  const hidden = useMemo(() => {
    const locked = sortableWidgets.filter((widget) => widget.locked);
    if (locked.length === 0) return hiddenProp;
    return { ...hiddenProp, ...Object.fromEntries(locked.map((widget) => [widget.type, true])) };
  }, [hiddenProp, sortableWidgets]);
  const allOrderedWidgets = useMemo(() => {
    const reconciled = reconcileWidgetOrder(sectionOrder, sortableWidgets);
    return reconciled
      .map((type) => sortableWidgets.find((widget) => widget.type === type))
      .filter((widget): widget is SummaryWidgetStackItem => Boolean(widget));
  }, [sectionOrder, sortableWidgets]);
  const visibleWidgets = useMemo(
    () => allOrderedWidgets.filter((widget) => !hidden[widget.type]),
    [allOrderedWidgets, hidden],
  );
  // Which widgets are currently ONE action row (their body registered a
  // `WidgetEmptyAction` and has nothing else). Reported by the mounted cards,
  // so it is knowledge of the stack as rendered.
  const [actionRows, setActionRows] = useState<Record<string, boolean>>({});
  const reportActionRow = useCallback((type: string, actionRow: boolean) => {
    setActionRows((current) =>
      (current[type] ?? false) === actionRow ? current : { ...current, [type]: actionRow },
    );
  }, []);
  /** The visible sections with content, in the type's order. */
  const sectionWidgets = useMemo(
    () => visibleWidgets.filter((widget) => !actionRows[widget.type]),
    [actionRows, visibleWidgets],
  );
  /** The visible empty sections, in the DEFAULT order (the order the surface
   *  declared them), under the run — the same sequence on every drawer. */
  const emptyWidgets = useMemo(
    () => sortableWidgets.filter((widget) => !hidden[widget.type] && actionRows[widget.type]),
    [actionRows, hidden, sortableWidgets],
  );
  /** Everything visible as rendered: the run, then the action rows. */
  const stackedWidgets = useMemo(
    () => [...sectionWidgets, ...emptyWidgets],
    [emptyWidgets, sectionWidgets],
  );

  return {
    allOrderedWidgets,
    visibleWidgets,
    sectionWidgets,
    emptyWidgets,
    stackedWidgets,
    reportActionRow,
  };
}

/** Separate the property list from the sections below it.
 * The fold control owns its row. The divider adds only section spacing:
 * the same distance from the fold row above (its 4px body inset + the
 * 1px card edge + the 4px stack gap + 8px here = 17px) as to the first
 * section capsule below (12px here + the 4px gap + the 1px card edge). */
export function SummarySectionDivider() {
  return (
    <Box paddingTop="sm" paddingBottom="base">
      <Separator />
    </Box>
  );
}

export function SummaryWidgetStack({
  gap = "xs",
  ...props
}: SummaryWidgetSurfaceProps & Omittable<{
  /** Vertical rhythm between stacked sections. The default `xs` stacks a
   *  32px section capsule (inside the card's 1px transparent edges) at the
   *  property rows' 38px pitch — a collapsed section or an action row is one
   *  line in the same list as the property rows, not a card with a margin. */
  gap?: "xs" | "sm" | "md" | "lg";
}>) {
  const { fixedWidget, additionalFixedWidgets = [] } = props;
  const selection = useSummaryWidgetSelection(props);
  // The viewer's folds, by section type, on every project (and, once they
  // exist, on every Account and Contact summary too).
  const { collapsedSet, setCollapsed } = useCollapsedSummarySections();

  return (
    // Symmetric `lg` insets matching the profile header's own, so every
    // stacked widget shares the header's content edges on both sides. The
    // right inset also keeps tiles and tables clear of the floating tab rail
    // gutter (top-right) — only the cover banner bleeds under the rail.
    <Box gap={gap} paddingLeft="lg" paddingRight="lg">
      <WidgetCard
        instance={fixedWidget}
        canManage={false}
        presentation="stack"
        surface="canvas"
        hideHeader={fixedWidget.hideHeader}
      />
      {additionalFixedWidgets.map((instance) => (
        <WidgetCard
          key={instance.id}
          instance={instance}
          canManage={false}
          presentation="stack"
          surface="canvas"
          collapsible={instance.collapsible}
          hideHeader={instance.hideHeader}
          collapsed={collapsedSet.has(instance.type)}
          onCollapsedChange={(isCollapsed) => setCollapsed(instance.type, isCollapsed)}
        />
      ))}
      <SummarySectionDivider />
      {/* ONE list, keyed by type: the sections with content in the type's
        * order, then the empty sections as "Add …" rows in default order. */}
      {selection.stackedWidgets.map((instance) => (
        <SummaryWidgetStackCard
          key={instance.id}
          instance={instance}
          collapsed={collapsedSet.has(instance.type)}
          onCollapsedChange={(isCollapsed) => setCollapsed(instance.type, isCollapsed)}
          onActionRowChange={selection.reportActionRow}
        />
      ))}
    </Box>
  );
}
